import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/CommonDateRangePicker.scss";

// Single-date counterpart to CommonDateRangePicker — same bordered shell,
// calendar icon, and popup styling (.date_range_wrapper/.drp-* classes,
// already global), just one field instead of a start+end pair. There was
// no shared component for a single date anywhere in the app; every caller
// that needed one (e.g. VendorProfileModal.js's Send Questionnaire expiry
// field) reached for a bare styled <input type="date"> instead. This is
// that missing common component.
const toISODate = (date) => (date ? date.toLocaleDateString("en-CA") : "");

const CalendarIcon = ({ className = "drp-icon" }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");
const toISO = ({ y, m, d }) => `${y}-${pad(m + 1)}-${pad(d)}`;
const fromDate = (dt) => ({ y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() });
const num = ({ y, m, d }) => y * 10000 + m * 100 + d;
const daysIn = (y, m) => new Date(y, m + 1, 0).getDate();
const addDays = (c, n) => fromDate(new Date(c.y, c.m, c.d + n));
const addMonths = (c, n) => {
  const dt = new Date(c.y, c.m + n, 1);
  return { y: dt.getFullYear(), m: dt.getMonth(), d: Math.min(c.d, daysIn(dt.getFullYear(), dt.getMonth())) };
};
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  return y && m && d ? { y, m: m - 1, d } : null;
};

const Chevron = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === "left" && <polyline points="15 18 9 12 15 6" />}
    {dir === "right" && <polyline points="9 18 15 12 9 6" />}
    {dir === "down" && <polyline points="6 9 12 15 18 9" />}
  </svg>
);

function MonthYearCalendar({ value, onChange, minDate, maxDate, placeholder = "Select date", disabled = false, ariaLabel, floating = false, highlight = [] }) {
  const highlightSet = useMemo(() => new Set(highlight), [highlight]);
  const selected = parseISO(value);
  const min = parseISO(minDate);
  const max = parseISO(maxDate);
  const today = fromDate(new Date());

  const clamp = (c) => {
    if (min && num(c) < num(min)) return min;
    if (max && num(c) > num(max)) return max;
    return c;
  };
  const monthOut = (y, m) =>
    (min && num({ y, m, d: daysIn(y, m) }) < num(min)) || (max && num({ y, m, d: 1 }) > num(max));

  const [open, setOpen] = useState(false);
  const [view, setView] = useState("days");
  const [cursor, setCursor] = useState(() => {
    const c = clamp(selected || today);
    return { y: c.y, m: c.m };
  });
  const [pickYear, setPickYear] = useState(cursor.y);
  const [focusDate, setFocusDate] = useState(null);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const gridRef = useRef(null);
  const yearsRef = useRef(null);
  const panelRef = useRef(null);
  const focusNow = useRef(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [host, setHost] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      const t = e.target;
      if (rootRef.current && !rootRef.current.contains(t) && !(panelRef.current && panelRef.current.contains(t))) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  useEffect(() => {
    if (!focusNow.current || !focusDate || !gridRef.current) return;
    focusNow.current = false;
    const btn = gridRef.current.querySelector(`[data-key="${num(focusDate)}"]`);
    if (btn) btn.focus();
  }, [focusDate, cursor]);

  useLayoutEffect(() => {
    if (!open || !floating) return undefined;
    const place = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const w = panel.offsetWidth;
      const h = panel.offsetHeight;
      const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - w - 8));
      let top = r.bottom + 8;
      if (top + h > window.innerHeight - 8 && r.top - h - 8 >= 8) top = r.top - h - 8;
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, floating, host]);

  useEffect(() => {
    if (view !== "months" || !yearsRef.current) return;
    const el = yearsRef.current.querySelector('[aria-selected="true"]');
    if (el) yearsRef.current.scrollTop = el.offsetTop - yearsRef.current.clientHeight / 2 + el.offsetHeight / 2;
  }, [view]);

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1).getDay();
    return Array.from({ length: 42 }, (_, i) => fromDate(new Date(cursor.y, cursor.m, 1 - first + i)));
  }, [cursor.y, cursor.m]);

  const yearFrom = min ? min.y : today.y - 100;
  const yearTo = max ? max.y : today.y + 20;
  const years = useMemo(
    () => Array.from({ length: Math.max(0, yearTo - yearFrom + 1) }, (_, i) => yearFrom + i),
    [yearFrom, yearTo],
  );

  const inGrid = (c) => grid.some((g) => num(g) === num(c));
  const roving =
    (focusDate && inGrid(focusDate) && focusDate) ||
    (selected && inGrid(selected) && selected) ||
    (inGrid(today) && today) ||
    { y: cursor.y, m: cursor.m, d: 1 };

  const openCalendar = () => {
    const c = clamp(selected || today);
    setCursor({ y: c.y, m: c.m });
    setView("days");
    setFocusDate(null);
    setHost(floating && triggerRef.current ? triggerRef.current.closest('[role="dialog"]') || document.body : null);
    setOpen(true);
  };

  const monthAt = (n) => {
    const dt = new Date(cursor.y, cursor.m + n, 1);
    return { y: dt.getFullYear(), m: dt.getMonth() };
  };
  const prevOut = monthOut(monthAt(-1).y, monthAt(-1).m);
  const nextOut = monthOut(monthAt(1).y, monthAt(1).m);

  const step = (n) => {
    const next = monthAt(n);
    if (monthOut(next.y, next.m)) return;
    setCursor(next);
    setFocusDate(null);
  };

  const pickDay = (c) => {
    if ((min && num(c) < num(min)) || (max && num(c) > num(max))) return;
    onChange(toISO(c));
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const toggleView = () => {
    if (view === "days") {
      setPickYear(cursor.y);
      setView("months");
    } else {
      setView("days");
    }
  };

  const pickMonth = (m) => {
    if (monthOut(pickYear, m)) return;
    setCursor({ y: pickYear, m });
    setFocusDate(null);
    setView("days");
  };

  const onGridKeyDown = (e) => {
    const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    let next;
    if (delta) next = addDays(roving, delta);
    else if (e.key === "PageUp") next = addMonths(roving, -1);
    else if (e.key === "PageDown") next = addMonths(roving, 1);
    else return;
    e.preventDefault();
    next = clamp(next);
    focusNow.current = true;
    setCursor({ y: next.y, m: next.m });
    setFocusDate(next);
  };

  const onPanelKeyDown = (e) => {
    if (e.key !== "Escape") return;
    e.stopPropagation();
    if (view === "months") {
      setView("days");
    } else {
      setOpen(false);
      if (triggerRef.current) triggerRef.current.focus();
    }
  };

  const label = selected
    ? new Date(selected.y, selected.m, selected.d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : placeholder;
  const inMonths = view === "months";
  const withHost = (node) => (floating && host ? createPortal(node, host) : node);

  return (
    <div className="cmn-cal" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`cmn-cal__trigger${selected ? "" : " is-placeholder"}${open ? " is-open" : ""}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openCalendar())}
      >
        <CalendarIcon className="cmn-cal__icon" />
        <span className="cmn-cal__value">{label}</span>
      </button>

      {open && !disabled && withHost(
        <div ref={panelRef} className={`cmn-cal__panel${floating ? " is-floating" : ""}`} style={floating ? pos : undefined} role="dialog" aria-label="Choose date" onKeyDown={onPanelKeyDown}>
          <div className="cmn-cal__head">
            <button type="button" className="cmn-cal__nav" aria-label="Previous month"
              disabled={inMonths || prevOut}
              onClick={() => step(-1)}>
              <Chevron dir="left" />
            </button>
            <button type="button" className={`cmn-cal__title${inMonths ? " is-open" : ""}`}
              aria-label="Choose month and year" aria-expanded={inMonths} onClick={toggleView}>
              <span>{MONTH_NAMES[cursor.m]} {cursor.y}</span>
              <Chevron dir="down" />
            </button>
            <button type="button" className="cmn-cal__nav" aria-label="Next month"
              disabled={inMonths || nextOut}
              onClick={() => step(1)}>
              <Chevron dir="right" />
            </button>
          </div>

          {!inMonths && (
            <>
              <div className="cmn-cal__weekdays" aria-hidden="true">
                {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
              </div>
              <div className="cmn-cal__grid" role="grid" ref={gridRef} onKeyDown={onGridKeyDown}>
                {grid.map((c) => {
                  const k = num(c);
                  const out = (min && k < num(min)) || (max && k > num(max));
                  const cls = [
                    "cmn-cal__day",
                    c.m !== cursor.m && "is-outside",
                    k === num(today) && "is-today",
                    selected && k === num(selected) && "is-selected",
                    highlightSet.has(toISO(c)) && "is-highlighted",
                    out && "is-disabled",
                  ].filter(Boolean).join(" ");
                  return (
                    <button key={k} type="button" className={cls} data-key={k}
                      tabIndex={k === num(roving) ? 0 : -1}
                      disabled={out}
                      aria-pressed={!!(selected && k === num(selected))}
                      aria-label={new Date(c.y, c.m, c.d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      onClick={() => pickDay(c)}>
                      {c.d}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {inMonths && (
            <div className="cmn-cal__picker">
              <div className="cmn-cal__months">
                {MONTH_NAMES.map((name, i) => (
                  <button key={name} type="button"
                    className={`cmn-cal__month${cursor.y === pickYear && cursor.m === i ? " is-selected" : ""}${today.y === pickYear && today.m === i ? " is-today" : ""}`}
                    disabled={monthOut(pickYear, i)}
                    onClick={() => pickMonth(i)}>
                    {name.slice(0, 3)}
                  </button>
                ))}
              </div>
              <div className="cmn-cal__years" ref={yearsRef} role="listbox" aria-label="Year">
                {years.map((y) => (
                  <button key={y} type="button" role="option"
                    className={`cmn-cal__year${y === pickYear ? " is-selected" : ""}`}
                    aria-selected={y === pickYear}
                    onClick={() => setPickYear(y)}>
                    {y}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommonDatePicker({ value, onChange, minDate, maxDate, placeholder = "Select date", disabled = false, portalId, inline = false, monthYearPanel = false, highlight }) {
  const dateObj = value ? new Date(value) : null;
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Inline mode: the calendar renders in the normal document flow directly below
  // the field (not as an absolute popper), so it can never be clipped by, or
  // spill out of, a scrolling modal — the container just grows to fit it. Used
  // where the picker lives inside a constrained modal (e.g. the Trend modal).
  useEffect(() => {
    if (!inline || !open) return;
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [inline, open]);

  if (monthYearPanel) {
    return (
      <MonthYearCalendar
        value={value}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        placeholder={placeholder}
        disabled={disabled}
        floating={!inline}
        highlight={highlight}
      />
    );
  }

  if (inline) {
    const label = dateObj
      ? dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      : placeholder;
    return (
      <div className="drp-inline" ref={rootRef}>
        <button
          type="button"
          className={`date_range_wrapper drp-inline-trigger${dateObj ? "" : " is-placeholder"}`}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <CalendarIcon />
          <span className="drp-inline-value">{label}</span>
        </button>

        {open && !disabled && (
          <div className="drp-inline-cal">
            <DatePicker
              inline
              selected={dateObj}
              onChange={(date) => { onChange(toISODate(date)); setOpen(false); }}
              minDate={minDate ? new Date(minDate) : null}
              maxDate={maxDate ? new Date(maxDate) : null}
              calendarClassName="drp-calendar"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="date_range_wrapper">
      <CalendarIcon />

      <DatePicker
        selected={dateObj}
        onChange={(date) => onChange(toISODate(date))}
        minDate={minDate ? new Date(minDate) : null}
        maxDate={maxDate ? new Date(maxDate) : null}
        dateFormat="dd MMM yyyy"
        placeholderText={placeholder}
        disabled={disabled}
        className="drp-input"
        calendarClassName="drp-calendar"
        popperClassName="drp-popper"
        wrapperClassName="drp-field"
        // Inside a modal whose body clips overflow, pass a portalId so the
        // calendar mounts in a <body> portal — it still anchors right below the
        // field (via popper) but can't be cut off by the modal's overflow.
        portalId={portalId}
      />
    </div>
  );
}

export default CommonDatePicker;
